import { useState } from "react";
import {
    DndContext,
    closestCenter,
    DragEndEvent,
    useSensor,
    useSensors,
    PointerSensor,
    TouchSensor,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { put_BulkSubLessonOrder } from "../services/SubLesson";
import useAuth from "../hook/useAuth";

type SubLessonOrderProps = {
    subLessonData: any[];
};

type Item = {
    id: number;
    subLessonId: number;
    contentId: number;
    contentTitle: string;
    contentUrl: string;
    videoId: number;
    videoTitle: string;
    orderNumber: number;
};

export const SubLessonOrder = ({ subLessonData }: SubLessonOrderProps) => {
    const { auth }: any = useAuth();

    const [items, setItems] = useState<Item[]>(() =>
        [...subLessonData].sort((a, b) => a.orderNumber - b.orderNumber)
    );

    /* -------------------- SENSORS (DESKTOP + MOBILE) -------------------- */
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        })
    );

    /* -------------------- SORTABLE ITEM -------------------- */
    const SortableItem = ({ item }: { item: Item }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
        } = useSortable({ id: item.id });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
        };

        return (
            <div
                ref={setNodeRef}
                style={style}
                {...attributes}
                {...listeners}
                className="mb-2 w-full rounded-md shadow-sm border border-gray-50 p-2 bg-white cursor-move touch-none"
            >
                <div className="flex items-center gap-2.5">
                    {item?.contentId > 0 && (
                        <>
                            <span className="material-symbols-outlined text-2xl text-gray-800">
                                docs
                            </span>
                            <span className="text-sm text-gray-800 font-semibold">
                                {item.contentTitle}
                            </span>
                        </>
                    )}

                    {item?.videoId > 0 && (
                        <>
                            <span className="material-symbols-outlined text-2xl text-gray-800">
                                videocam
                            </span>
                            <span className="text-sm text-gray-800 font-semibold">
                                {item.videoTitle}
                            </span>
                        </>
                    )}
                </div>
            </div>
        );
    };

    /* -------------------- DRAG END HANDLER -------------------- */
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);

        const reordered = arrayMove(items, oldIndex, newIndex).map(
            (item, idx) => ({
                ...item,
                orderNumber: idx + 1,
            })
        );

        setItems(reordered);
        updateSubLessonOrder(reordered);
    };

    /* -------------------- API UPDATE -------------------- */
    const updateSubLessonOrder = async (updatedOrder: Item[]) => {
        try {
            await put_BulkSubLessonOrder(updatedOrder, auth?.id);
        } catch (error) {
            console.error("Error updating order:", error);
        }
    };

    /* -------------------- RENDER -------------------- */
    return (
        <div className="w-full">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={items.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map((item) => (
                        <SortableItem key={item.id} item={item} />
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
};
